import React from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

/**
 * Shared building blocks for the filter bar above list tables. Every filter
 * clears itself in place — there is no separate reset control — so dropping one
 * choice never discards the rest.
 */

export const filterSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    borderColor: "#ced4da",
    borderRadius: ".25rem",
    boxShadow: "none",
    "&:hover": { borderColor: "#ced4da" },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderColor: "#ced4da",
    borderRadius: ".25rem",
  }),
};

export const FilterLabel = ({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}) => (
  <label
    htmlFor={htmlFor}
    className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
  >
    {children}
  </label>
);

const ClearButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
    onClick={onClick}
  >
    <i className="ri-close-circle-fill fs-16"></i>
  </button>
);

export const FilterSearchBox = ({
  id = "filter-search",
  label = "Search",
  placeholder = "Search...",
  value,
  onChange,
}: {
  id?: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <>
    <FilterLabel htmlFor={id}>{label}</FilterLabel>
    <div className="search-box">
      <input
        id={id}
        type="text"
        className="form-control search"
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        value={value || ""}
      />
      <i className="ri-search-line search-icon"></i>
      {value ? (
        <ClearButton label="Clear search" onClick={() => onChange("")} />
      ) : null}
    </div>
  </>
);

export type DateRangeValue = {
  fromDate: Date | null;
  toDate: Date | null;
};

export const FilterDateRange = ({
  id = "filter-date-range",
  label = "Date Range",
  placeholder = "Select a date range",
  value,
  onChange,
}: {
  id?: string;
  label?: string;
  placeholder?: string;
  value: DateRangeValue | undefined;
  onChange: (value: DateRangeValue) => void;
}) => (
  <>
    <FilterLabel htmlFor={id}>{label}</FilterLabel>
    <div className="position-relative">
      {/* @ts-ignore */}
      <Flatpickr
        id={id}
        className="form-control"
        placeholder={placeholder}
        options={{ mode: "range", altFormat: "d M, Y", dateFormat: "d M, Y" }}
        onChange={(dates: Date[]) => {
          if (dates[0] && dates[1]) {
            onChange({ fromDate: dates[0], toDate: dates[1] });
          }
        }}
        value={[value?.fromDate || "", value?.toDate || ""]}
      />
      {value?.fromDate ? (
        <ClearButton
          label="Clear date range"
          onClick={() => onChange({ fromDate: null, toDate: null })}
        />
      ) : null}
    </div>
  </>
);

/**
 * A clearable dropdown holding the whole `{ value, label }` option, so the
 * choice survives a reload from the URL without a second lookup.
 */
export const FilterSelect = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  options,
  isLoading,
}: {
  id: string;
  label?: string;
  placeholder: string;
  value: any;
  onChange: (value: any) => void;
  options: any[];
  isLoading?: boolean;
}) => (
  <>
    {label ? <FilterLabel htmlFor={id}>{label}</FilterLabel> : null}
    <Select
      name={id}
      inputId={id}
      value={value?.value ? value : null}
      placeholder={placeholder}
      onChange={onChange}
      options={options}
      isLoading={isLoading}
      isClearable
      styles={filterSelectStyles}
    />
  </>
);

/**
 * The same dropdown for the pages whose filter state is a bare id rather than
 * an option object; it resolves the id against the loaded options and emits a
 * bare id back (empty string when cleared).
 */
export const FilterValueSelect = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  options,
  isLoading,
}: {
  id: string;
  label?: string;
  placeholder: string;
  value: any;
  onChange: (value: any) => void;
  options: any[];
  isLoading?: boolean;
}) => (
  <FilterSelect
    id={id}
    label={label}
    placeholder={placeholder}
    options={options}
    isLoading={isLoading}
    value={
      options.find((option: any) => String(option.value) === String(value)) ||
      null
    }
    onChange={(option: any) => onChange(option?.value ?? "")}
  />
);
